// @@QUOTES_READY_v106@@ Open Quotes — open Zoho Books estimates across every
// customer. "Ready to Invoice" files a to-do (with notes) in the Ready Quote List.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';

const STATUS_LABEL = {
  draft: 'Draft', sent: 'Sent', viewed: 'Viewed', accepted: 'Accepted',
  pending_approval: 'Pending approval', approved: 'Approved', partially_invoiced: 'Partially invoiced',
};
const FILTERS = [
  { k: 'all', label: 'All open' },
  { k: 'draft', label: 'Draft' },
  { k: 'sent', label: 'Sent' },
  { k: 'accepted', label: 'Accepted' },
  { k: 'ready', label: 'Ready to invoice' },
  { k: 'unmarked', label: 'Not marked' },
];

function money(n, ccy) {
  if (n == null) return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: ccy || 'USD' }).format(n); }
  catch (e) { return '$' + Number(n).toFixed(2); }
}
// YYYY-MM-DD read by string slice — never new Date(iso) (Phoenix shows the day before).
function day(s) {
  if (!s) return '—';
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  if (!y) return '—';
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
}
function stamp(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch (e) { return ''; }
}
function matchesFilter(q, f) {
  if (f === 'all') return true;
  if (f === 'ready') return !!q.ready;
  if (f === 'unmarked') return !q.ready;
  if (f === 'sent') return q.status === 'sent' || q.status === 'viewed';
  return q.status === f;
}

function StatusPill({ q }) {
  return (
    <span className="hq-v106-status-wrap">
      <span className={'hq-pill hq-v106-st hq-v106-st-' + q.status}>{STATUS_LABEL[q.status] || q.status}</span>
      {q.past_expiry && <span className="hq-v106-exp">Past expiry</span>}
    </span>
  );
}

function Detail({ q }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  useEffect(() => {
    let on = true;
    apiFetch('/api/quotes/' + encodeURIComponent(q.estimate_id))
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!on) return;
        if (r.ok) setD(j.estimate); else setErr(r.status === 503 ? 'Could not reach Zoho.' : (j.error || 'Could not load this quote.'));
      })
      .catch(() => { if (on) setErr('Could not reach the server.'); });
    return () => { on = false; };
  }, [q.estimate_id]);

  async function pdf() {
    setPdfBusy(true);
    try {
      const r = await apiFetch('/api/quotes/' + encodeURIComponent(q.estimate_id) + '/pdf?n=' + encodeURIComponent(q.number));
      if (!r.ok) { setErr('PDF unavailable right now.'); return; }
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = url; a.download = 'XYZ-Estimate-' + q.number.replace(/[^A-Za-z0-9._-]/g, '_') + '.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { setErr('PDF unavailable right now.'); }
    finally { setPdfBusy(false); }
  }

  if (err) return <div className="hq-v106-detail"><p className="hq-v106-err">{err}</p></div>;
  if (!d) return <div className="hq-v106-detail"><p className="hq-v106-muted">Loading line items…</p></div>;
  const ccy = d.currency_code;
  const extra = [
    ['Subtotal', d.sub_total], ['Discount', d.discount_total ? -Math.abs(d.discount_total) : null],
    ['Shipping', d.shipping_charge || null], ['Tax', d.tax_total || null], ['Adjustment', d.adjustment || null],
  ].filter(([, v]) => v != null && v !== 0);
  return (
    <div className="hq-v106-detail">
      <div className="hq-v106-lines">
        <table>
          <thead><tr><th>Item</th><th className="r">Qty</th><th className="r">Rate</th><th className="r">Amount</th></tr></thead>
          <tbody>
            {d.line_items.map((li, i) => (
              <tr key={i}>
                <td><div className="hq-v106-li-name">{li.name || '—'}</div>{li.description && <div className="hq-v106-muted hq-v106-pre">{li.description}</div>}</td>
                <td className="r">{li.quantity != null ? li.quantity : '—'}{li.unit ? ' ' + li.unit : ''}</td>
                <td className="r">{money(li.rate, ccy)}</td>
                <td className="r">{money(li.amount, ccy)}</td>
              </tr>
            ))}
            {d.line_items.length === 0 && <tr><td colSpan={4} className="hq-v106-muted">No line items.</td></tr>}
          </tbody>
          <tfoot>
            {extra.map(([k, v]) => <tr key={k}><td colSpan={3} className="r">{k}</td><td className="r">{money(v, ccy)}</td></tr>)}
            <tr className="hq-v106-total"><td colSpan={3} className="r">Total</td><td className="r">{money(d.total, ccy)}</td></tr>
          </tfoot>
        </table>
      </div>
      <div className="hq-v106-side">
        {d.salesperson && <p><span className="hq-v106-k">Salesperson</span>{d.salesperson}</p>}
        {d.notes && <p><span className="hq-v106-k">Customer notes</span><span className="hq-v106-pre">{d.notes}</span></p>}
        {d.terms && <p><span className="hq-v106-k">Terms</span><span className="hq-v106-pre">{d.terms}</span></p>}
        {q.ready && q.ready.notes && <p><span className="hq-v106-k">Ready-to-invoice notes</span><span className="hq-v106-pre">{q.ready.notes}</span></p>}
        <button className="hq-btn hq-btn-ghost" onClick={pdf} disabled={pdfBusy}>{pdfBusy ? 'Loading…' : 'Download PDF'}</button>
      </div>
    </div>
  );
}

function ReadyModal({ q, assignees, onClose, onDone }) {
  const [notes, setNotes] = useState('');
  const [assignee, setAssignee] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const r = await apiFetch('/api/quotes/' + encodeURIComponent(q.estimate_id) + '/ready', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes, assignee_ids: assignee ? [assignee] : [] }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok) { onDone(j); return; }
      if (r.status === 409 && j.error === 'already') { onDone({ ready: j.ready, already: true }); return; }
      setErr(
        j.error === 'not_open' ? 'This quote is no longer open in Zoho (' + (STATUS_LABEL[j.status] || j.status) + '). Refresh the list.'
          : j.error === 'migration_missing' ? 'The database migration for this feature has not been applied yet.'
            : r.status === 503 ? 'Could not reach Zoho — try again in a minute.'
              : (j.error || 'Could not mark this quote.'));
    } catch (e2) { setErr('Could not reach the server.'); }
    finally { setBusy(false); }
  }
  return (
    <div className="hq-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <form className="hq-modal" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="v106-ready-title">
        <div className="hq-modal-head">
          <h2 id="v106-ready-title">Ready to Invoice</h2>
          <button type="button" className="hq-modal-x" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </div>
        <div className="hq-modal-body">
          <div className="hq-v106-sum">
            <div><span className="hq-v106-k">Quote</span>{q.number}</div>
            <div><span className="hq-v106-k">Customer</span>{q.customer_name || '—'}</div>
            <div><span className="hq-v106-k">Total</span>{money(q.total, q.currency_code)}</div>
          </div>
          <label className="hq-modal-label" htmlFor="v106-notes">Notes</label>
          <textarea id="v106-notes" className="hq-input hq-v106-textarea" rows={5} maxLength={4000} autoFocus
            placeholder="Anything the person invoicing needs to know — deposit, ship date, changes from the quote…"
            value={notes} onChange={(e) => setNotes(e.target.value)} />
          <label className="hq-modal-label" htmlFor="v106-assignee">Assign to (optional)</label>
          <select id="v106-assignee" className="hq-select hq-v106-full" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">Unassigned</option>
            {assignees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <p className="hq-modal-hint">Adds a to-do to the <strong>Ready Quote List</strong> with these notes. The assignee gets an email.</p>
          {err && <p className="hq-v106-err">{err}</p>}
        </div>
        <div className="hq-modal-foot">
          <button type="button" className="hq-btn hq-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="hq-btn hq-v106-go" disabled={busy}>{busy ? 'Adding…' : 'Ready to Invoice'}</button>
        </div>
      </form>
    </div>
  );
}

export default function Quotes() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(null);
  const [modal, setModal] = useState(null);
  const [assignees, setAssignees] = useState([]);

  const load = useCallback(async (fresh) => {
    if (fresh) setBusy(true); else setLoading(true);
    try {
      const r = await apiFetch('/api/quotes' + (fresh ? '?fresh=1' : ''));
      const j = await r.json().catch(() => ({}));
      if (r.ok) setData(j); else setData({ available: false, reason: 'error', quotes: [] });
    } catch (e) { setData({ available: false, reason: 'error', quotes: [] }); }
    finally { setLoading(false); setBusy(false); }
  }, []);
  useEffect(() => { load(false); }, [load]);
  useEffect(() => {
    apiFetch('/api/tasks/assignees').then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j) setAssignees(j.assignees || []); }).catch(() => {});
  }, []);

  const quotes = useMemo(() => (data && data.quotes) || [], [data]);
  const counts = useMemo(() => {
    const c = {};
    for (const f of FILTERS) c[f.k] = quotes.filter((q) => matchesFilter(q, f.k)).length;
    return c;
  }, [quotes]);
  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    return quotes.filter((q) => matchesFilter(q, filter)).filter((q) => !s
      || [q.number, q.customer_name, q.client_name, q.reference, q.salesperson].some((v) => v && String(v).toLowerCase().includes(s)));
  }, [quotes, filter, search]);
  const openTotal = useMemo(() => shown.reduce((a, q) => a + (Number(q.total) || 0), 0), [shown]);

  function onReady(q, j) {
    setModal(null);
    setData((d) => ({ ...d, quotes: d.quotes.map((x) => (x.estimate_id === q.estimate_id ? { ...x, ready: j.ready } : x)) }));
    setMsg(j.already
      ? { text: q.number + ' was already marked ready.', task: j.ready && j.ready.task_id }
      : { text: q.number + ' added to the Ready Quote List.', task: j.ready && j.ready.task_id, list: j.list_id });
  }

  const unavailable = data && !data.available;
  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Open Quotes</h1>
        <p>Open estimates from Zoho Books across every customer. “Ready to Invoice” adds a to-do with your notes to the Ready Quote List.</p>
      </div>

      <div className="hq-panel">
        <div className="hq-panel-head">
          <h2>Quotes{data && data.available ? <span className="hq-v106-count">{shown.length} · {money(openTotal, 'USD')}</span> : null}</h2>
          <div className="hq-v106-headacts">
            {data && data.fetched_at && <span className="hq-v106-muted hq-v106-small">Updated {stamp(data.fetched_at)}</span>}
            <button className="hq-btn hq-btn-ghost" onClick={() => load(true)} disabled={busy || loading}>{busy ? 'Syncing…' : 'Refresh from Zoho'}</button>
          </div>
        </div>
        {msg && (
          <div className="hq-v106-flash">
            <span>{msg.text}</span>
            {msg.task && <Link to={'/tasks/' + msg.task}>Open to-do</Link>}
            {msg.list && <Link to={'/tasks/lists/' + msg.list}>View list</Link>}
            <button type="button" className="hq-modal-x" onClick={() => setMsg(null)} aria-label="Dismiss">×</button>
          </div>
        )}
        <div className="hq-v101-toolbar">
          <div className="hq-v101-seg hq-v106-seg">
            {FILTERS.map((f) => (
              <button key={f.k} type="button" className={filter === f.k ? 'on' : ''} onClick={() => setFilter(f.k)}>
                {f.label}{data && data.available ? ' (' + counts[f.k] + ')' : ''}
              </button>
            ))}
          </div>
          <input className="hq-input hq-v106-search" type="search" placeholder="Search quote #, customer, reference…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {data && data.truncated && <p className="hq-v106-warn">Showing the newest 2,000 estimates from Zoho — older open quotes may be missing.</p>}

        {loading ? (
          <p className="hq-empty">Loading quotes from Zoho…</p>
        ) : unavailable ? (
          <p className="hq-empty">{data.reason === 'unconfigured' ? 'Zoho is not configured on this server.' : 'Could not reach Zoho right now. Try “Refresh from Zoho” in a minute.'}</p>
        ) : (
          <div className="hq-v106-scroll">
            <table className="hq-table hq-v106-table">
              <thead>
                <tr>
                  <th>Quote #</th><th>Customer</th><th className="hq-v106-hide-sm">Date</th><th className="hq-v106-hide-sm">Expires</th>
                  <th className="hq-v106-hide-md">Reference</th><th>Status</th><th className="r">Total</th><th className="hq-v106-actcol" />
                </tr>
              </thead>
              <tbody>
                {shown.map((q) => (
                  <React.Fragment key={q.estimate_id}>
                    <tr className={'hq-v106-row' + (open === q.estimate_id ? ' is-open' : '')}
                      onClick={() => setOpen(open === q.estimate_id ? null : q.estimate_id)}>
                      <td className="hq-v106-num"><span className="hq-v106-caret" aria-hidden="true">▸</span>{q.number}</td>
                      <td className="hq-v106-c-cust">
                        <div className="hq-v106-cust">{q.customer_name || '—'}</div>
                        {q.client_id && <Link className="hq-v106-client" to={'/clients/' + q.client_id} onClick={(e) => e.stopPropagation()}>{q.client_name}</Link>}
                      </td>
                      <td className="hq-v106-hide-sm hq-v106-date">{day(q.date)}</td>
                      <td className="hq-v106-hide-sm hq-v106-date">{day(q.expiry_date)}</td>
                      <td className="hq-v106-hide-md">{q.reference || '—'}</td>
                      <td className="hq-v106-c-status"><StatusPill q={q} /></td>
                      <td className="r hq-v106-amt">{money(q.total, q.currency_code)}</td>
                      <td className="hq-v106-actcol" onClick={(e) => e.stopPropagation()}>
                        {q.ready ? (
                          <div className="hq-v106-ready">
                            <span className={'hq-v106-chip' + (q.ready.task_done ? ' is-done' : '')}>{q.ready.task_done ? 'To-do done ✓' : 'Ready ✓'}</span>
                            <Link to={'/tasks/' + q.ready.task_id}>To-do</Link>
                            <div className="hq-v106-by">{q.ready.by ? q.ready.by + ' · ' : ''}{stamp(q.ready.at)}</div>
                          </div>
                        ) : (
                          <button type="button" className="hq-btn hq-v106-go" onClick={() => setModal(q)}>Ready to Invoice</button>
                        )}
                      </td>
                    </tr>
                    {open === q.estimate_id && (
                      <tr className="hq-v106-detailrow"><td colSpan={8}><Detail q={q} /></td></tr>
                    )}
                  </React.Fragment>
                ))}
                {shown.length === 0 && (
                  <tr><td className="hq-empty" colSpan={8}>{quotes.length ? 'No quotes match.' : 'No open quotes in Zoho.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {modal && <ReadyModal q={modal} assignees={assignees} onClose={() => setModal(null)} onDone={(j) => onReady(modal, j)} />}
    </div>
  );
}
